import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WagmiProvider } from "wagmi";
import { PrivyProvider } from "@privy-io/react-auth";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { wagmiConfig } from "@/lib/wagmi-config";
import { SovereignNav } from "@/components/sovereign/nav";
import { DashboardSidebar } from "@/components/sovereign/dashboard-layout";
import { ProtectedRoute } from "@/components/sovereign/protected-route";
import { RenewalBanner } from "@/components/sovereign/renewal-banner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PrivacyProvider } from "@/hooks/use-privacy-mode";
import { AuthProvider } from "@/hooks/use-auth";
import { PrivyTokenBridge } from "@/components/sovereign/privy-token-bridge";
import { ChainEnforcer } from "@/components/sovereign/chain-enforcer";
import { LaunchGate } from "@/components/sovereign/launch-gate";
import { tenantConfig } from "@/lib/sovereign";
import { trackPageView } from "@/lib/analytics";

const Landing = lazy(() => import("@/pages/landing"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const DashboardAI = lazy(() => import("@/pages/dashboard-ai"));
const DashboardProjections = lazy(() => import("@/pages/dashboard-projections"));
const DashboardActivity = lazy(() => import("@/pages/dashboard-activity"));
const DashboardHistory = lazy(() => import("@/pages/dashboard-history"));
const DashboardYield = lazy(() => import("@/pages/dashboard-yield"));
const DashboardTeam = lazy(() => import("@/pages/dashboard-team"));
const DashboardAnalytics = lazy(() => import("@/pages/dashboard-analytics"));
const DashboardRewards = lazy(() => import("@/pages/dashboard-rewards"));

const DashboardProtocol = lazy(() => import("@/pages/dashboard-protocol"));
const DashboardMarketing = lazy(() => import("@/pages/dashboard-marketing"));
const Onboarding = lazy(() => import("@/pages/onboarding"));
const Community = lazy(() => import("@/pages/community"));
const Calculator = lazy(() => import("@/pages/home"));
const Subscribe = lazy(() => import("@/pages/subscribe"));
const Guide = lazy(() => import("@/pages/guide"));
const Admin = lazy(() => import("@/pages/admin"));
const FranchiseDashboard = lazy(() => import("@/pages/franchise-dashboard"));
const SuperAdmin = lazy(() => import("@/pages/super-admin"));
const Franchise = lazy(() => import("@/pages/franchise"));
const Settings = lazy(() => import("@/pages/settings"));
const Terms = lazy(() => import("@/pages/terms"));
const NotFound = lazy(() => import("@/pages/not-found"));

function RouteLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
        <p className="text-sm text-slate-400">Loading...</p>
      </div>
    </div>
  );
}

function PageViewTracker() {
  const [location] = useLocation();
  useEffect(() => {
    trackPageView(location);
  }, [location]);
  return null;
}

function FeatureGate({ flag, children }: { flag: keyof typeof tenantConfig.features; children: ReactNode }) {
  if (tenantConfig.features[flag] === false) return <Redirect to="/" />;
  return <>{children}</>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 300000, refetchOnWindowFocus: false, retry: 1 },
  },
});

function DashboardRoutes() {
  return (
    <DashboardSidebar>
      <Suspense fallback={<RouteLoader />}>
        <Switch>
          <Route path="/dashboard">
            <Dashboard />
          </Route>
          <Route path="/dashboard/ai">
            <FeatureGate flag="aiAdvisor"><DashboardAI /></FeatureGate>
          </Route>
          <Route path="/dashboard/projections">
            <FeatureGate flag="projections"><DashboardProjections /></FeatureGate>
          </Route>
          <Route path="/dashboard/activity">
            <FeatureGate flag="activity"><DashboardActivity /></FeatureGate>
          </Route>
          <Route path="/dashboard/history">
            <FeatureGate flag="balanceHistory"><DashboardHistory /></FeatureGate>
          </Route>
          <Route path="/dashboard/team">
            <FeatureGate flag="spiderWeb"><DashboardTeam /></FeatureGate>
          </Route>
          <Route path="/dashboard/analytics">
            <FeatureGate flag="analytics"><DashboardAnalytics /></FeatureGate>
          </Route>
          <Route path="/dashboard/rewards">
            <FeatureGate flag="rewardsHub"><DashboardRewards /></FeatureGate>
          </Route>
          <Route path="/dashboard/yield">
            <FeatureGate flag="yieldTools"><DashboardYield /></FeatureGate>
          </Route>
          <Route path="/dashboard/protocol">
            <FeatureGate flag="protocolOverview"><DashboardProtocol /></FeatureGate>
          </Route>
          <Route path="/dashboard/marketing">
            <DashboardMarketing />
          </Route>
          <Route path="/dashboard/calculator">
            <FeatureGate flag="calculator"><Calculator /></FeatureGate>
          </Route>
          <Route path="/dashboard/calculator/yield">
            <FeatureGate flag="calculator"><Calculator /></FeatureGate>
          </Route>
          <Route path="/dashboard/calculator/spiderweb">
            <FeatureGate flag="calculator"><Calculator /></FeatureGate>
          </Route>
          <Route>
            <Redirect to="/dashboard" />
          </Route>
        </Switch>
      </Suspense>
    </DashboardSidebar>
  );
}

function Router() {
  return (
    <>
      <PageViewTracker />
      <SovereignNav />
      <RenewalBanner />
      <main id="main-content" className="flex flex-col">
        <Suspense fallback={<RouteLoader />}>
          <Switch>
            <Route path="/" component={Landing} />
            <Route path="/dashboard/calculator/yield">
              <ProtectedRoute><DashboardRoutes /></ProtectedRoute>
            </Route>
            <Route path="/dashboard/calculator/spiderweb">
              <ProtectedRoute><DashboardRoutes /></ProtectedRoute>
            </Route>
            <Route path="/dashboard/:page">
              <ProtectedRoute><DashboardRoutes /></ProtectedRoute>
            </Route>
            <Route path="/dashboard">
              <ProtectedRoute><DashboardRoutes /></ProtectedRoute>
            </Route>
            <Route path="/onboarding" component={Onboarding} />
            <Route path="/community">
              <FeatureGate flag="communityChannels"><Community /></FeatureGate>
            </Route>
            <Route path="/calculator">
              <Redirect to="/dashboard/calculator" />
            </Route>
            <Route path="/subscribe" component={Subscribe} />
            <Route path="/guide" component={Guide} />
            <Route path="/admin" component={Admin} />
            <Route path="/franchise/dashboard" component={FranchiseDashboard} />
            <Route path="/super-admin" component={SuperAdmin} />
            <Route path="/franchise" component={Franchise} />
            <Route path="/settings" component={Settings} />
            <Route path="/terms" component={Terms} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </main>
    </>
  );
}

// Prefer the Vercel env var; the fallback is the owner-controlled Privy app
// created 2026-07-10 after access to the original app was lost (TOB-175).
const PRIVY_APP_ID =
  (import.meta.env.VITE_PRIVY_APP_ID as string | undefined) ?? "cmreejtc7006y0djzruilgvu0";

function App() {
  return (
    <MotionConfig reducedMotion="user">
    <ErrorBoundary>
      <PrivyProvider
        appId={PRIVY_APP_ID}
        config={{
          loginMethods: ["wallet"],
          embeddedWallets: {
            ethereum: { createOnLogin: "off" },
          },
          appearance: { theme: "dark", accentColor: "#22c55e" },
        }}
      >
        <WagmiProvider config={wagmiConfig}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <PrivyTokenBridge />
              <ChainEnforcer />
              <PrivacyProvider>
                <TooltipProvider>
                <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                  <LaunchGate>
                    <Router />
                  </LaunchGate>
                </WouterRouter>
                  <Toaster />
                </TooltipProvider>
              </PrivacyProvider>
            </AuthProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </PrivyProvider>
    </ErrorBoundary>
    </MotionConfig>
  );
}

export default App;
